import React, { useState } from 'react';
import { Trash2, ArrowDown, Menu, X } from 'lucide-react';

// TypeScript interfaces for the component
interface ChatControlsProps {
  onClearChat?: () => void;
  onScrollToBottom?: () => void;
  onToggleChat?: () => void;
  onCloseChat?: () => void;
  className?: string;
  style?: React.CSSProperties;
  showScrollButton?: boolean;
  confirmClear?: boolean;
  disabled?: boolean;
  clearLabel?: string;
}

/**
 * ChatControls component for managing chat actions
 * Converted to use Tailwind CSS classes and Lucide React icons for V2 specification compliance
 */
const ChatControls: React.FC<ChatControlsProps> = ({
  onClearChat,
  onScrollToBottom,
  onToggleChat,
  onCloseChat,
  className,
  style,
  showScrollButton = true,
  confirmClear = true,
  disabled = false,
  clearLabel = 'Clear'
}) => {
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Handle clear chat click
  const handleClearChat = () => {
    if (disabled) return;
    
    if (confirmClear) {
      setShowClearConfirm(true);
    } else if (onClearChat) {
      onClearChat();
    }
  };

  // Handle clear chat confirmation
  const handleConfirmClear = () => {
    if (onClearChat) {
      onClearChat();
    }
    setShowClearConfirm(false);
  };

  // Handle cancel clear
  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };
  return (
    <div 
      data-testid="chat-controls"
      className={`
        relative flex gap-2 p-2 border-t border-gray-200 bg-white
        ${className || ''}
      `}
      style={style}
    >
      <button 
        data-testid="clear-chat-button" 
        onClick={handleClearChat}
        disabled={disabled}
        type="button"
        className={`
          px-2 py-1 text-xs border border-gray-300 rounded bg-white
          cursor-pointer min-h-[44px] min-w-[44px]
          hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          transition-colors duration-200
          flex items-center justify-center gap-1
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        aria-label="Clear chat history"
      >
        <Trash2 size={16} className="text-gray-600" />
        {clearLabel && <span className="text-gray-600">{clearLabel}</span>}
      </button>
      {showScrollButton && (
        <button 
          data-testid="scroll-to-bottom-button" 
          onClick={onScrollToBottom}
          className={`
            px-2 py-1 text-xs border border-gray-300 rounded bg-white
            cursor-pointer min-h-[44px] min-w-[44px]
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
            transition-colors duration-200
            flex items-center justify-center
          `}
          aria-label="Scroll to bottom of chat"
        >
          <ArrowDown size={16} className="text-gray-600" />
        </button>
      )}
      <button 
        data-testid="toggle-chat-button" 
        onClick={onToggleChat}
        className={`
          px-2 py-1 text-xs border border-gray-300 rounded bg-white
          cursor-pointer min-h-[44px] min-w-[44px]
          hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          transition-colors duration-200
          flex items-center justify-center
        `}
        aria-label="Toggle chat visibility"
      >
        <Menu size={16} className="text-gray-600" />
      </button>
      <button 
        data-testid="close-chat-button" 
        onClick={onCloseChat}
        className={`
          px-2 py-1 text-xs border border-gray-300 rounded bg-white
          cursor-pointer min-h-[44px] min-w-[44px]
          hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
          transition-colors duration-200
          flex items-center justify-center
        `}
        aria-label="Close chat"
      >
        <X size={16} className="text-gray-600" />
      </button>

      {/* Clear chat confirmation dialog */}
      {showClearConfirm && (
        <div 
          data-testid="clear-confirm-dialog" 
          className="absolute bottom-full left-0 right-0 mb-2 p-4 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
        >
          <p className="text-sm text-gray-700 mb-3">Are you sure you want to clear the chat history?</p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              data-testid="clear-confirm-yes"
              onClick={handleConfirmClear}
              className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Yes, clear chat
            </button>
            <button
              type="button"
              data-testid="clear-confirm-no"
              onClick={handleCancelClear}
              className="px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatControls;
export type { ChatControlsProps }; 